import '../styles/Policies/Termsofuse.css';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const clauses = [
  {
    id: 'acceptance',
    title: 'Acceptance of Terms',
    body: `By accessing or using the Actyra website, publications, or any associated research materials, you agree to be bound by these Terms of Use. If you do not agree with any part of these terms, you should not access or use our services. These terms apply to all visitors, subscribers, research partners, and consulting clients of Actyra.`,
  },
  {
    id: 'nature-of-content',
    title: 'Nature of Research Content',
    body: `Actyra is an independent actuarial research and thought leadership firm. All content published on this platform — including whitepapers, technical notes, actuarial insight briefs, industry commentary, and public dashboards — is intended for informational and educational purposes within professional actuarial, insurance, pension, and risk management contexts.`,
    note: `Research outputs do not constitute professional actuarial advice, legal advice, investment advice, or regulatory guidance. Readers are encouraged to seek independent professional advice before making decisions based on Actyra research.`,
  },
  {
    id: 'intellectual-property',
    title: 'Intellectual Property',
    body: `All content, research outputs, brand elements, models, methodologies, and materials published by Actyra are the exclusive intellectual property of Actyra and its authors. This includes, but is not limited to:`,
    items: [
      'Whitepapers, technical notes, and actuarial insight briefs',
      'Proprietary modelling frameworks and methodologies including IFRS 17 analysis, capital modelling approaches, and mortality study outputs',
      'Public dashboards, data visualisations, and interactive tools',
      'Conference papers and regulatory response submissions',
      'The Actyra brand name, logo, tagline, and design elements',
    ],
    footer: `You may not reproduce, republish, redistribute, or commercially exploit any Actyra content without prior written permission. Limited non-commercial citation is permitted with full attribution.`,
  },
  {
    id: 'permitted-use',
    title: 'Permitted Use',
    body: `You are permitted to:`,
    items: [
      'Access and read published research for personal or internal professional use',
      'Share links to Actyra publications with appropriate attribution',
      'Reference Actyra research in academic or professional work, citing Actyra as the source',
      'Subscribe to and receive Actyra research communications you have opted into',
    ],
    footer: `You may not scrape, mirror, systematically copy, or automate retrieval of content from this platform.`,
  },
  {
    id: 'advisory-services',
    title: 'Advisory & Consulting Services',
    body: `Where Actyra provides IFRS 17 advisory, capital and solvency advisory, independent model validation, or product and profitability advisory services, a separate engagement agreement will govern the terms of that relationship. These Terms of Use apply to all website interactions and research access independent of any such agreement.

Actyra advisory services are selective and research-led. Engagement in any consulting relationship does not imply endorsement of any particular methodology or regulatory position beyond what is expressly stated in the relevant deliverable.`,
  },
  {
    id: 'accuracy',
    title: 'Accuracy and Limitations',
    body: `Actyra takes significant care to ensure research accuracy and methodological rigour. However, actuarial analysis involves assumptions, models, and projections that are inherently uncertain. Actyra makes no warranties, express or implied, regarding:`,
    items: [
      'The completeness, accuracy, or timeliness of any research output',
      'The suitability of research findings for any specific organisational or regulatory context',
      'The outcomes of strategies or decisions informed by Actyra publications',
      'The applicability of research findings to any jurisdiction not explicitly addressed',
    ],
  },
  {
    id: 'limitation-of-liability',
    title: 'Limitation of Liability',
    body: `To the fullest extent permitted by applicable law, Actyra, its directors, employees, and research contributors shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from the use of, or reliance on, any content published on this platform or delivered through our services. This includes, without limitation, financial loss, regulatory penalty, or reputational damage.`,
  },
  {
    id: 'third-party',
    title: 'Third-Party Links',
    body: `Our publications and website may reference or link to third-party sources, including regulatory bodies, academic institutions, and industry organisations. Actyra does not endorse, control, or assume responsibility for the content of third-party websites or publications. External links are provided for research context only.`,
  },
  {
    id: 'modifications',
    title: 'Modifications to Terms',
    body: `Actyra reserves the right to update these Terms of Use at any time. Changes will be published on this page with an updated effective date. Your continued use of the platform following any such changes constitutes your acceptance of the revised terms. Where changes are material, we will notify subscribers by email.`,
  },
  {
    id: 'governing-law',
    title: 'Governing Law',
    body: `These Terms of Use are governed by the laws of Kenya. Any disputes arising in connection with these terms or the use of Actyra services shall be subject to the exclusive jurisdiction of the courts of Kenya, unless otherwise agreed in a separate engagement agreement.`,
  },
  {
    id: 'contact',
    title: 'Contact',
    body: `For questions regarding these Terms of Use, or to request permissions for content use, please contact:\n\nActyra — Legal\nEmail: legal@actyra.com`,
  },
];

export default function TermsofUse() {
  const sectionRefs = useRef([]);
  const [activeId, setActiveId] = useState(clauses[0].id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('tu-visible');
            setActiveId(entry.target.id);
          }
        });
      },
      { threshold: 0.2, rootMargin: '-80px 0px -60% 0px' }
    );
    sectionRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const effectiveDate = 'May 2025';

  return (
    <div className="tu-root">
      {/* Background */}
      <div className="tu-bg" aria-hidden="true">
        <div className="tu-bg-texture" />
        <div className="tu-bg-bar tu-bg-bar--left" />
        <div className="tu-bg-bar tu-bg-bar--right" />
        <div className="tu-bg-glow" />
      </div>

      {/* Header */}
      <header className="tu-header">
        <div className="tu-header-inner">
          <Link to="/" className="tu-back">
            <span className="tu-back-arrow">←</span>
            <span>Back to Actyra</span>
          </Link>

          <div className="tu-header-strap">
            <span className="tu-strap-line" />
            <span className="tu-strap-text">Legal Framework</span>
            <span className="tu-strap-line" />
          </div>

          <h1 className="tu-title">Terms of Use</h1>

          <div className="tu-header-sub">
            <p className="tu-intro">
              These terms govern your access to Actyra's research, publications, and advisory services. Please read carefully.
            </p>
            <div className="tu-date-block">
              <span className="tu-date-label">Effective</span>
              <span className="tu-date-value">{effectiveDate}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="tu-layout">
        {/* Sidebar */}
        <aside className="tu-sidebar">
          <div className="tu-sidebar-inner">
            <div className="tu-sidebar-title">Sections</div>
            {clauses.map((c, i) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                className={`tu-sidebar-link ${activeId === c.id ? 'tu-sidebar-link--active' : ''}`}
              >
                <span className="tu-sidebar-idx">{i + 1}</span>
                <span className="tu-sidebar-name">{c.title}</span>
              </a>
            ))}
          </div>
        </aside>

        {/* Main content */}
        <main className="tu-content">
          {clauses.map((clause, i) => (
            <article
              key={clause.id}
              id={clause.id}
              className="tu-clause tu-reveal"
              ref={(el) => (sectionRefs.current[i] = el)}
            >
              <div className="tu-clause-header">
                <div className="tu-clause-num">{String(i + 1).padStart(2, '0')}</div>
                <h2 className="tu-clause-title">{clause.title}</h2>
              </div>

              <div className="tu-clause-body">
                {clause.body && (
                  <p className="tu-prose">
                    {clause.body.split('\n\n').map((para, j, arr) => (
                      <span key={j}>
                        {para}
                        {j < arr.length - 1 && <><br /><br /></>}
                      </span>
                    ))}
                  </p>
                )}

                {clause.note && (
                  <div className="tu-callout">
                    <div className="tu-callout-bar" />
                    <p className="tu-callout-text">{clause.note}</p>
                  </div>
                )}

                {clause.items && (
                  <ul className="tu-list">
                    {clause.items.map((item, ii) => (
                      <li key={ii} className="tu-list-item">
                        <span className="tu-tick">–</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                )}

                {clause.footer && (
                  <p className="tu-prose tu-prose--em">{clause.footer}</p>
                )}
              </div>
            </article>
          ))}

          <div className="tu-bottom-nav">
            <Link to="/privacy" className="tu-bottom-link">← Privacy Policy</Link>
            <Link to="/data-usage" className="tu-bottom-link">Data Usage Policy →</Link>
          </div>
        </main>
      </div>
    </div>
  );
}